import { useEffect, useMemo, useState } from "react";
import { Polygon, Marker, Polyline } from "react-leaflet";
import L from "leaflet";
import { api, apiError } from "@/lib/api";
import LeafletMap, { FitBounds } from "@/components/map/LeafletMap";
import { classifyBoundary } from "@/lib/geoClassify";
import PhotoUpload from "@/components/PhotoUpload";
import PhotoGallery from "@/components/content/PhotoGallery";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { MapPin, Megaphone, Milestone, Plus, Trash2, Pencil, X, Loader2, Undo2, MousePointerClick, Eye, EyeOff, Clock } from "lucide-react";
import { toast } from "sonner";

const KINDS = [
  { key: "attraction", label: "Attractions", icon: MapPin, geo: "point", extras: ["website", "phone", "address", "hours"] },
  { key: "advertiser", label: "Advertisers", icon: Megaphone, geo: "point", extras: ["website", "phone", "address", "hours"] },
  { key: "detour", label: "Detours", icon: Milestone, geo: "point" },
];

function inPolygon(lat, lng, poly) {
  if (!poly || poly.length < 3) return true;
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const yi = poly[i][0], xi = poly[i][1], yj = poly[j][0], xj = poly[j][1];
    if (((yi > lat) !== (yj > lat)) && (lng < ((xj - xi) * (lat - yi)) / ((yj - yi) || 1e-12) + xi)) inside = !inside;
  }
  return inside;
}

const CATEGORY_PRESETS = {
  attraction: ["Park", "Scenic View", "Landmark", "Historic Site", "Beach", "Playground", "Food", "Other"],
  advertiser: ["Food", "Cafe", "Bike Shop", "Services", "Retail", "Lodging", "Rentals", "Other"],
  detour: ["Construction", "Scenic", "Shortcut", "Hazard", "Trail Closure", "Other"],
  route: ["Scenic", "Family-friendly", "Training", "Commuter", "Loop", "Other"],
};
const UNCLASSIFIED = "Unclassified";
const stateOf = (p) => classifyBoundary(p.boundary || []).state || UNCLASSIFIED;

const dot = (color) => L.divIcon({ className: "", iconSize: [16, 16], iconAnchor: [8, 8], html: `<div style="width:16px;height:16px;border-radius:9999px;background:${color};border:2px solid #fff;box-shadow:0 0 0 1px rgba(0,0,0,.3);cursor:grab"></div>` });

const EMPTY = { id: null, name: "", description: "", category: "", website: "", phone: "", address: "", hours: "", difficulty: "", est_time: "", photos: [], lat: null, lng: null, path: [], waypoint_labels: [] };

export default function ContentManager({ mode = "local", embedBikePathId = null }) {
  const [areas, setAreas] = useState([]);
  const [areaId, setAreaId] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [items, setItems] = useState([]);
  const [kind, setKind] = useState("attraction");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [presets, setPresets] = useState(CATEGORY_PRESETS);

  const area = useMemo(() => areas.find((a) => a.id === areaId), [areas, areaId]);
  const kindDef = KINDS.find((k) => k.key === kind);

  // Global mode only: derive each bike path's US state so admins can filter the
  // Bike path list by State (mirrors the Global Route Index behaviour).
  const withState = useMemo(() => (mode === "global" ? areas.map((a) => ({ ...a, _state: stateOf(a) })) : areas), [areas, mode]);
  const states = useMemo(() => (mode === "global" ? Array.from(new Set(withState.map((a) => a._state))).sort() : []), [withState, mode]);
  const visibleAreas = useMemo(() => (mode === "global" && stateFilter ? withState.filter((a) => a._state === stateFilter) : withState), [withState, stateFilter, mode]);

  const loadAreas = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(mode === "local" ? "/my/zones" : "/bike-paths");
      setAreas(data);
      if (data.length && !areaId) setAreaId(data[0].id);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const loadItems = async () => {
    if (!areaId) return setItems([]);
    try {
      const params = mode === "local" ? {} : { scope: "global", bike_path_id: areaId };
      const { data } = await api.get("/content", { params });
      const filtered = mode === "local" ? data.filter((d) => d.zone_id === areaId) : data;
      setItems(filtered);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  useEffect(() => { loadAreas(); }, [mode]);
  useEffect(() => { if (embedBikePathId) setAreaId(embedBikePathId); }, [embedBikePathId, areas.length]);
  useEffect(() => { api.get("/content-categories").then((r) => { if (r.data && !Array.isArray(r.data) && Object.keys(r.data).length) setPresets(r.data); }).catch(() => {}); }, []);
  useEffect(() => { loadItems(); setEditing(null); }, [areaId]);
  useEffect(() => { if (mode === "global" && states.length && (!stateFilter || !states.includes(stateFilter))) setStateFilter(states[0]); }, [states, mode]);
  const visibleKey = useMemo(() => visibleAreas.map((a) => a.id).join(","), [visibleAreas]);
  useEffect(() => {
    if (mode !== "global" || embedBikePathId || !visibleAreas.length) return;
    if (!visibleAreas.some((a) => a.id === areaId)) setAreaId(visibleAreas[0].id);
  }, [stateFilter, visibleKey]);

  const geoOk = (form) => {
    if (!area) return false;
    const poly = mode === "local" ? (area.polygon || []) : (area.boundary || []);
    if (kindDef.geo === "line") {
      if (!form.path.length) return true;
      return form.path.every((p) => inPolygon(p[0], p[1], poly));
    }
    if (form.lat == null) return true;
    return inPolygon(form.lat, form.lng, poly);
  };

  const mapClick = (lat, lng) => {
    if (!editing) return;
    if (kindDef.geo === "line") setEditing((s) => ({ ...s, path: [...s.path, [lat, lng]], waypoint_labels: [...(s.waypoint_labels || []), { name: "", category: "" }] }));
    else setEditing((s) => ({ ...s, lat, lng }));
  };

  const startNew = () => setEditing({ ...EMPTY });
  const startEdit = (it) => setEditing({ id: it.id, name: it.name, description: it.description || "", category: it.category || "", website: it.website || "", phone: it.phone || "", address: it.address || "", hours: it.hours || "", difficulty: it.difficulty || "", est_time: it.est_time || "", photos: it.photos || [], lat: it.lat ?? null, lng: it.lng ?? null, path: it.path || [], waypoint_labels: it.waypoint_labels || [] });

  const save = async () => {
    const f = editing;
    if (!f.name.trim()) return toast.error("Name is required");
    if (kindDef.geo === "line") { if (f.path.length < 2) return toast.error("Draw at least 2 points on the map"); }
    else if (f.lat == null) return toast.error("Click the map to set a location");
    if (!geoOk(f)) return toast.error(mode === "local" ? "Location must stay inside the selected zone" : "Location must stay inside the bike path boundary");
    setSaving(true);
    try {
      const body = {
        kind, scope: mode, name: f.name, description: f.description, category: f.category,
        website: f.website, phone: f.phone, address: f.address, hours: f.hours, difficulty: f.difficulty, est_time: f.est_time,
        photos: f.photos, lat: f.lat, lng: f.lng, path: f.path, waypoint_labels: f.waypoint_labels,
        ...(mode === "local" ? { zone_id: areaId } : { bike_path_id: areaId }),
      };
      if (f.id) await api.put(`/content/${f.id}`, body);
      else await api.post("/content", body);
      const pendingNote = !f.id && (kind === "attraction" || kind === "advertiser") && mode === "local";
      toast.success(f.id ? "Saved" : (pendingNote ? `Created — pending admin approval before it shows to riders` : "Created"));
      setEditing(null); loadItems();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (id) => { try { await api.delete(`/content/${id}`); toast.success("Deleted"); loadItems(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const toggleVis = async (id) => { try { await api.patch(`/content/${id}/visibility`); loadItems(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };

  if (loading) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  if (!areas.length) return <div className="rounded-2xl border border-dashed border-border bg-card p-12 text-center text-muted-foreground" data-testid="content-no-areas">{mode === "local" ? "No service area assigned to your shop yet. Velopaths will assign your zone(s) soon." : "Create a bike path first to add global content."}</div>;

  const kindItems = items.filter((i) => i.kind === kind);
  const areaPoly = mode === "local" ? (area?.polygon || []) : (area?.boundary || []);
  const bounds = areaPoly.length >= 2 ? areaPoly : null;
  const center = areaPoly.length ? areaPoly[0] : [41.75, -71.3];
  const badGeo = editing && !geoOk(editing);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        {!embedBikePathId && mode === "global" && (
        <div className="w-48">
          <Label>State</Label>
          <Select value={stateFilter} onValueChange={setStateFilter} disabled={!states.length}>
            <SelectTrigger data-testid="content-state-select"><SelectValue placeholder={states.length ? "Select a state" : "No states"} /></SelectTrigger>
            <SelectContent>{states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        )}
        {!embedBikePathId && (
        <div className="w-64">
          <Label>{mode === "local" ? "Zone" : "Bike path"}</Label>
          <Select value={areaId} onValueChange={setAreaId}>
            <SelectTrigger data-testid="content-area-select"><SelectValue placeholder={mode === "global" && !visibleAreas.length ? "No bike paths in this state" : undefined} /></SelectTrigger>
            <SelectContent>{visibleAreas.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}{mode === "local" && a.bike_path_name ? ` · ${a.bike_path_name}` : ""}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        )}
        <div className="flex flex-wrap gap-1.5">
          {KINDS.map((k) => (
            <button key={k.key} onClick={() => { setKind(k.key); setEditing(null); }} data-testid={`content-kind-${k.key}`}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${kind === k.key ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-secondary"}`}>
              <k.icon className="w-4 h-4" />{k.label}
            </button>
          ))}
        </div>
        <div className="ml-auto">
          {mode === "local" && kind === "advertiser" ? null
            : !editing ? <Button onClick={startNew} data-testid="content-new-button"><Plus className="w-4 h-4 mr-1" />New {kindDef.label.slice(0, -1)}</Button>
            : <Button variant="ghost" onClick={() => setEditing(null)} data-testid="content-cancel-button"><X className="w-4 h-4 mr-1" />Cancel</Button>}
        </div>
      </div>

      {editing && (
        <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-3" data-testid="content-form">
          <div className="text-sm text-muted-foreground flex items-center gap-2"><MousePointerClick className="w-4 h-4 text-primary shrink-0" />{kindDef.geo === "line" ? "Click the map to add points, then drag any point to refine the route." : "Click the map to place the location, then drag the pin to fine-tune."} {mode === "local" ? "It must stay inside your zone." : "It must stay inside the bike path."}</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Name</Label><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="content-name-input" /></div>
            <div><Label>Category</Label>
              <Select value={editing.category || ""} onValueChange={(v) => setEditing({ ...editing, category: v })}>
                <SelectTrigger data-testid="content-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>{(presets[kind] || []).map((cat) => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Description</Label><Textarea value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} rows={2} data-testid="content-description-input" /></div>
          {kindDef.extras?.includes("website") && (
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Website</Label><Input value={editing.website} onChange={(e) => setEditing({ ...editing, website: e.target.value })} placeholder="https://" data-testid="content-website-input" /></div>
              <div><Label>Phone</Label><Input value={editing.phone} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} data-testid="content-phone-input" /></div>
            </div>
          )}
          {kindDef.extras?.includes("address") && (
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Address</Label><Input value={editing.address} onChange={(e) => setEditing({ ...editing, address: e.target.value })} placeholder="123 Thames St, Bristol, RI" data-testid="content-address-input" /></div>
              <div><Label>Hours</Label><Input value={editing.hours} onChange={(e) => setEditing({ ...editing, hours: e.target.value })} placeholder="Mon–Sun 8am–6pm" data-testid="content-hours-input" /></div>
            </div>
          )}
          {kindDef.extras?.includes("difficulty") && (
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Difficulty</Label>
                <Select value={editing.difficulty || "none"} onValueChange={(v) => setEditing({ ...editing, difficulty: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="content-difficulty-select"><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">—</SelectItem>{["Easy", "Moderate", "Hard"].map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Estimated time</Label><Input value={editing.est_time} onChange={(e) => setEditing({ ...editing, est_time: e.target.value })} placeholder="e.g. 25 min" data-testid="content-est-time-input" /></div>
            </div>
          )}
          <div><Label className="mb-2 block">Photos (optional)</Label><PhotoUpload photos={editing.photos} onChange={(photos) => setEditing({ ...editing, photos })} testId="content-photos" /></div>
          {kindDef.geo === "line" && (
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => setEditing((s) => ({ ...s, path: s.path.slice(0, -1), waypoint_labels: (s.waypoint_labels || []).slice(0, -1) }))} disabled={!editing.path.length} data-testid="content-undo-point"><Undo2 className="w-4 h-4 mr-1" />Undo point</Button>
              <span className="text-sm text-muted-foreground" data-testid="content-point-count">{editing.path.length} point{editing.path.length === 1 ? "" : "s"}</span>
            </div>
          )}
          {kindDef.geo === "line" && editing.path.length > 0 && (
            <div className="space-y-2 max-h-48 overflow-y-auto rounded-lg border border-border p-2" data-testid="waypoint-labels">
              <div className="text-xs font-semibold text-muted-foreground">Waypoint labels (optional)</div>
              {editing.path.map((_, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <span className="text-xs w-6 text-muted-foreground">#{i + 1}</span>
                  <Input value={editing.waypoint_labels?.[i]?.name || ""} maxLength={40} placeholder="e.g. Scenic overlook" onChange={(e) => setEditing((s) => { const wl = [...(s.waypoint_labels || [])]; while (wl.length < s.path.length) wl.push({ name: "", category: "" }); wl[i] = { ...wl[i], name: e.target.value }; return { ...s, waypoint_labels: wl }; })} data-testid={`waypoint-name-${i}`} />
                  <Select value={editing.waypoint_labels?.[i]?.category || "none"} onValueChange={(v) => setEditing((s) => { const wl = [...(s.waypoint_labels || [])]; while (wl.length < s.path.length) wl.push({ name: "", category: "" }); wl[i] = { ...wl[i], category: v === "none" ? "" : v }; return { ...s, waypoint_labels: wl }; })}>
                    <SelectTrigger className="w-28 h-9" data-testid={`waypoint-cat-${i}`}><SelectValue placeholder="Category" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">—</SelectItem>{["Scenic", "Food", "Rest", "Info", "Safety"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          )}
          <div className={`text-xs ${badGeo ? "text-destructive" : "text-muted-foreground"}`} data-testid="content-geo-status">
            {kindDef.geo === "line" ? (editing.path.length ? (badGeo ? "Some points are outside the boundary" : "All points inside boundary") : "No points yet") : (editing.lat != null ? (badGeo ? "Location is outside the boundary" : `Location: ${editing.lat.toFixed(5)}, ${editing.lng.toFixed(5)}`) : "No location set")}
          </div>
          <div className="flex justify-end"><Button onClick={save} disabled={saving} data-testid="content-save-button">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Save</Button></div>
        </div>
      )}

      <LeafletMap height={440} onClick={editing ? mapClick : undefined} center={center}>
        {mode === "local" && areaPoly.length >= 3 && <Polygon positions={areaPoly} pathOptions={{ color: "#EF4444", fillColor: "#FACC15", fillOpacity: 0.2, weight: 2 }} />}
        {mode === "global" && area?.boundary?.length >= 3 && <Polygon positions={area.boundary} pathOptions={{ color: "#64748B", weight: 2, fillOpacity: 0.04, dashArray: "6" }} />}
        {bounds && bounds.length >= 2 && <FitBounds bounds={bounds} />}
        {kindItems.map((it) => it.kind === "route"
          ? <Polyline key={it.id} positions={it.path} pathOptions={{ color: "#22D3EE" }} />
          : (it.lat != null && <Marker key={it.id} position={[it.lat, it.lng]} />))}
        {editing && kindDef.geo === "line" && editing.path.length >= 2 && <Polyline positions={editing.path} pathOptions={{ color: badGeo ? "#EF4444" : "#F59E0B" }} />}
        {editing && kindDef.geo === "line" && editing.path.map((p, i) => (
          <Marker key={i} position={p} draggable icon={dot(badGeo ? "#EF4444" : "#F59E0B")}
            eventHandlers={{ dragend: (e) => { const { lat, lng } = e.target.getLatLng(); setEditing((s) => { const path = [...s.path]; path[i] = [lat, lng]; return { ...s, path }; }); } }} />
        ))}
        {editing && kindDef.geo === "point" && editing.lat != null && (
          <Marker position={[editing.lat, editing.lng]} draggable icon={dot(badGeo ? "#EF4444" : "#F59E0B")}
            eventHandlers={{ dragend: (e) => { const { lat, lng } = e.target.getLatLng(); setEditing((s) => ({ ...s, lat, lng })); } }} />
        )}
      </LeafletMap>

      {mode === "local" && kind === "advertiser" && (
        <div className="rounded-xl border border-border bg-secondary/40 p-4 text-sm text-muted-foreground" data-testid="advertiser-readonly-note">
          Advertisers appear here once they sign up and pay through the advertiser form. You can view your paid advertisers but can't add them manually — this keeps every advertiser a paying Velopaths subscriber.
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="content-list">
        {kindItems.length === 0 ? <div className="text-sm text-muted-foreground">No {kindDef.label.toLowerCase()} yet.</div> : kindItems.map((it) => (
          <div key={it.id} className="rounded-2xl border border-border bg-card overflow-hidden" data-testid={`content-card-${it.id}`}>
            {it.photos?.length > 0 && <PhotoGallery photos={it.photos} height={112} testId={`content-gallery-${it.id}`} />}
            <div className="p-4">
              <div className="font-heading font-bold">{it.name}</div>
              {it.category && <div className="text-xs text-primary">{it.category}</div>}
              {it.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{it.description}</p>}
              {it.kind === "route" && <div className="text-xs text-muted-foreground mt-1">{it.difficulty || "—"}{it.est_time ? ` · ${it.est_time}` : ""} · {it.path?.length || 0} pts</div>}
              {it.source === "listing" ? (
                <div className="mt-3 text-xs text-primary font-medium" data-testid={`content-sponsored-${it.id}`}>Sponsored advertiser · managed in the Advertisers tab</div>
              ) : it.approval_status === "pending" ? (
              <div className="flex items-center justify-between mt-3">
                <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-500" data-testid={`content-pending-${it.id}`}><Clock className="w-4 h-4" />Pending admin approval</span>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => startEdit(it)} data-testid={`content-edit-${it.id}`}><Pencil className="w-4 h-4" /></Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild><Button variant="ghost" size="sm" className="text-destructive" data-testid={`content-delete-${it.id}`}><Trash2 className="w-4 h-4" /></Button></AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader><AlertDialogTitle>Delete "{it.name}"?</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                      <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => del(it.id)} data-testid={`content-confirm-delete-${it.id}`}>Delete</AlertDialogAction></AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
              ) : it.approval_status === "rejected" ? (
              <div className="mt-3">
                <span className="inline-flex items-center gap-1 text-xs font-medium text-red-500" data-testid={`content-rejected-${it.id}`}><X className="w-4 h-4" />Rejected by admin</span>
                {it.rejection_reason && <p className="text-xs text-muted-foreground mt-1" data-testid={`content-reject-reason-${it.id}`}>Reason: {it.rejection_reason}</p>}
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs text-muted-foreground">Edit to resubmit for review</span>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => startEdit(it)} data-testid={`content-edit-${it.id}`}><Pencil className="w-4 h-4" /></Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild><Button variant="ghost" size="sm" className="text-destructive" data-testid={`content-delete-${it.id}`}><Trash2 className="w-4 h-4" /></Button></AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Delete "{it.name}"?</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => del(it.id)} data-testid={`content-confirm-delete-${it.id}`}>Delete</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </div>
              ) : (
              <div className="flex items-center justify-between mt-3">
                <Button variant="ghost" size="sm" onClick={() => toggleVis(it.id)} className={it.visible !== false ? "text-primary" : "text-muted-foreground"} data-testid={`content-toggle-${it.id}`}>{it.visible !== false ? <Eye className="w-4 h-4 mr-1" /> : <EyeOff className="w-4 h-4 mr-1" />}{it.visible !== false ? "Visible" : "Hidden"}</Button>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => startEdit(it)} data-testid={`content-edit-${it.id}`}><Pencil className="w-4 h-4" /></Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild><Button variant="ghost" size="sm" className="text-destructive" data-testid={`content-delete-${it.id}`}><Trash2 className="w-4 h-4" /></Button></AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader><AlertDialogTitle>Delete "{it.name}"?</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                      <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => del(it.id)} data-testid={`content-confirm-delete-${it.id}`}>Delete</AlertDialogAction></AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
